import './Block.css';
import React, { useState, useEffect } from 'react';
import { getQuizBlocks, getQuizQuestions, getQuizChoices, getUserQuizAttempts } from '../../api/lessons';

function QuizBlock({ lessonPageBlockId, localQuizBlock, refreshTrigger, onAttempt }) {
    const [quizBlock, setQuizBlock] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [choices, setChoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selected, setSelected] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [feedback, setFeedback] = useState([]);

    const resolveChoices = (question, questionIndex) => {
        if (Array.isArray(question.choices) && question.choices.length > 0) return question.choices;
        if (Array.isArray(question.options) && question.options.length > 0) {
            return question.options.map((opt) => ({
                id: opt.id || null,
                text: opt.text || '',
                is_correct: !!(opt.is_correct ?? opt.isCorrect),
            }));
        }
        if (quizBlock?.id && Array.isArray(choices) && choices.length > 0) return choices.filter(c => c.quiz_question === question.id);
        return [];
    };

    useEffect(() => {
        async function fetchQuizData() {
            const hasLocalQuestions = Array.isArray(localQuizBlock?.questions) && localQuizBlock.questions.length > 0;

            if (localQuizBlock && hasLocalQuestions) {
                setQuizBlock(localQuizBlock);
                setQuestions(localQuizBlock.questions);
                setChoices([]);
                setLoading(false);
                setError(null);
                if (localQuizBlock.id != null) onAttempt?.({});
                return;
            }

            try {
                // Fetch all quiz blocks, then filter for this lesson-page-block
                const allQuizBlocks = await getQuizBlocks();
                const blockData = allQuizBlocks.find(b => b.lesson_page_block === lessonPageBlockId);
                setQuizBlock(blockData);
                // Fetch questions for this quiz block
                const allQuestions = await getQuizQuestions();
                const filteredQuestions = blockData ? allQuestions.filter(q => q.quiz_block === blockData.id).sort((a, b) => a.id - b.id) : [];
                const questionsWithChoices = await Promise.all(filteredQuestions.map(async (question) => {
                    const allQuestionChoices = await getQuizChoices(question.id);
                    return {
                        ...question,
                        type: question.choice_type || 'single',
                        choices: Array.isArray(allQuestionChoices) ? allQuestionChoices : [],
                    };
                }));
                setQuestions(questionsWithChoices);
                // Fetch all choices
                const allChoices = await getQuizChoices();
                setChoices(allChoices);

                // Check for existing successful attempt
                if (blockData) {
                    onAttempt?.({});
                    const attemptsList = await getUserQuizAttempts(blockData.id);
                    const successfulAttempt = attemptsList.find(a => a.completed && a.passed);
                    if (successfulAttempt) {
                        setSubmitted(true);
                        setAttempts(1);
                        // Pre-select correct answers and set feedback
                        const initialFeedback = filteredQuestions.map(() => 'Correct!');
                        setFeedback(initialFeedback);
                        const initialSelected = filteredQuestions.map((q) => {
                            const qChoices = allChoices.filter(c => c.quiz_question === q.id);
                            const qType = q.choice_type || 'single';
                            if (qType === 'single') return qChoices.findIndex(c => c.is_correct);
                            return qChoices.map((c, idx) => c.is_correct ? idx : null).filter(val => val !== null);
                        });
                        setSelected(initialSelected);
                    }
                }
                setLoading(false);
            } catch (err) {
                setError(err);
                setLoading(false);
            }
        }
        fetchQuizData();
    }, [lessonPageBlockId, localQuizBlock, refreshTrigger]);

    useEffect(() => {
        setSelected(questions.map(q => q.type === 'single' ? null : []));
        setFeedback(Array(questions.length).fill(''));
        setSubmitted(false);
        setShowAnswer(false);
        setAttempts(0);
    }, [questions]);

    if (loading) {
        return <div className="quiz-block-wrapper">Loading...</div>;
    }
    if (error) {
        return <div className="quiz-block-wrapper">Error loading quiz block/questions.</div>;
    }
    if (!Array.isArray(questions) || questions.length === 0) {
        return <div className="quiz-block-wrapper">No quiz data available.</div>;
    }

    const handleSelect = (qIdx, oIdx, type) => {
        if (submitted) return;
        setSelected(prev => {
            const updated = [...prev];
            if (type === 'single') {
                updated[qIdx] = oIdx;
            } else {
                updated[qIdx] = updated[qIdx].includes(oIdx)
                    ? updated[qIdx].filter(i => i !== oIdx)
                    : [...updated[qIdx], oIdx];
            }
            return updated;
        });
    };

    const handleSubmit = async () => {
        let hasAnyCorrect = false;
        let isOverallCorrect = true;
        const newFeedback = questions.map((q, qIdx) => {
            const qChoices = resolveChoices(q, qIdx);
            if (q.type === 'single') {
                // Find selected index
                const selectedIdx = selected[qIdx];
                if (selectedIdx !== null && selectedIdx !== undefined && qChoices[selectedIdx] && qChoices[selectedIdx].is_correct) {
                    hasAnyCorrect = true;
                    return 'Correct!';
                } else {
                    isOverallCorrect = false;
                    return 'Try Again';
                }
            } else {
                // Multiple choice logic
                let selectedCorrectCount = 0;
                let totalCorrectAvailable = 0;
                let anyWrongSelected = false;
                qChoices.forEach((opt, oIdx) => {
                    if (opt.is_correct) totalCorrectAvailable++;
                    const isSelected = (Array.isArray(selected[qIdx]) ? selected[qIdx] : []).includes(oIdx);
                    if (isSelected && opt.is_correct) {
                        selectedCorrectCount++;
                    }
                    if (isSelected && !opt.is_correct) {
                        anyWrongSelected = true;
                    }
                });
                if (
                    selectedCorrectCount === totalCorrectAvailable &&
                    totalCorrectAvailable > 0 &&
                    !anyWrongSelected
                ) {
                    hasAnyCorrect = true;
                    return 'Correct!';
                } else {
                    isOverallCorrect = false;
                    return 'Try Again';
                }
            }
        });
        setFeedback(newFeedback);
        setSubmitted(true);
        setAttempts(a => a + 1);

        // Register attempt (create or update via Chapter.jsx, which already ensured this
        // attempt exists as soon as the quiz loaded). Do NOT record per-question choices.
        if (quizBlock?.id != null) {
            const passed = newFeedback.every(f => f === 'Correct!');
            onAttempt?.({ completed: true, passed });
        }
    };

    const handleRetry = () => {
        setSubmitted(false);
        setShowAnswer(false);
        setFeedback(Array(questions.length).fill(''));
        setSelected(questions.map(q => q.type === 'single' ? null : []));
    };

    const handleShowAnswer = () => {
        setShowAnswer(true);
        setSubmitted(true);
        setFeedback(questions.map(() => 'Answer Revealed'));
    };

    return (
        <div className={`quiz-block-wrapper${submitted ? ' submitted' : ''}`}> 
            {questions.map((q, qIdx) => {
                const qChoices = resolveChoices(q, qIdx);
                return (
                    <div key={qIdx} className="quiz-question-container">
                        <div className="quiz-question-title">{q.text}</div>
                        <div className="quiz-options-list">
                            {(Array.isArray(qChoices) ? qChoices : []).map((opt, oIdx) => {
                                const isSelected = q.type === 'single'
                                    ? selected[qIdx] === oIdx
                                    : (selected[qIdx] || []).includes(oIdx);
                                // Show check if submitted and correct, or if showAnswer is true
                                const showCheck = (submitted && opt.is_correct && (showAnswer || feedback[qIdx] === 'Correct!'));
                                return (
                                    <div
                                        key={oIdx}
                                        className={`quiz-option${isSelected ? ' selected' : ''}${showCheck ? ' show-check' : ''}`}
                                        onClick={() => handleSelect(qIdx, oIdx, q.type)}
                                        style={{ cursor: submitted ? 'default' : 'pointer', background: showCheck ? 'var(--bg-secondary)' : undefined }}
                                    >
                                        <span className={`quiz-indicator ${q.type === 'single' ? 'circle' : 'square'}`}></span>
                                        {opt.text}
                                        {showCheck && <span style={{ marginLeft: 8, color: '#22c55e' }}>✔</span>}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={`quiz-feedback${feedback[qIdx] === 'Correct!' ? ' success' : feedback[qIdx] === 'Try Again' ? ' retry' : ''}`}>{feedback[qIdx]}</div>
                    </div>
                );
            })}
            <div className="quiz-footer" style={{ marginTop: 16 }}>
                {!submitted && (
                    <button className="btn-submit-quiz" onClick={handleSubmit}>Submit</button>
                )}
                {submitted && !showAnswer && !feedback.every(f => f === 'Correct!') && (
                    <button className="btn-retry-quiz" onClick={handleRetry}>Retry</button>
                )}
                {submitted && attempts >= 3 && !showAnswer && !feedback.every(f => f === 'Correct!') && (
                    <button className="btn-show-answer" onClick={handleShowAnswer}>Show Answer</button>
                )}
            </div>
        </div>
    );
}

export default QuizBlock;